import { PropAsset } from './PropAsset';

// Gate - Kapı
// Large gate/doorway in a wall
export class Gate extends PropAsset {
  readonly id = 'gate';
  readonly width = 80;
  readonly height = 100;
  readonly walkable = true; // Player can walk through the opening
  readonly collisionRadius = 0; // No collision in center
  readonly occludesPlayer = false;

  draw(ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number, variant?: number): void {
    const v = variant || 0;

    const gateColors = [
      { stone: '#6b6b6b', dark: '#4a4a4a', light: '#8a8a8a', wood: '#5a4030' }, // Stone gate
      { stone: '#8b7355', dark: '#5a4a3a', light: '#a08570', wood: '#4a3020' }, // Sandstone
      { stone: '#3a3a3a', dark: '#2a2a2a', light: '#5a5a5a', wood: '#2a2020' }, // Dark stone
    ];
    const colors = gateColors[v % gateColors.length];

    const gateWidth = 70 * s;
    const gateHeight = 80 * s;
    const pillarWidth = 16 * s;
    const archHeight = 25 * s;

    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.3)';
    ctx.beginPath();
    ctx.ellipse(cx, cy, 35 * s, 12 * s, 0, 0, Math.PI * 2);
    ctx.fill();

    // Left pillar - back face
    ctx.fillStyle = colors.dark;
    ctx.fillRect(cx - gateWidth / 2, cy - gateHeight, pillarWidth, gateHeight);

    // Right pillar - back face
    ctx.fillRect(cx + gateWidth / 2 - pillarWidth, cy - gateHeight, pillarWidth, gateHeight);

    // Dark opening (background)
    ctx.fillStyle = '#1a1510';
    ctx.beginPath();
    ctx.moveTo(cx - gateWidth / 2 + pillarWidth, cy);
    ctx.lineTo(cx - gateWidth / 2 + pillarWidth, cy - gateHeight + archHeight);
    // Arch
    ctx.quadraticCurveTo(cx, cy - gateHeight - 5 * s, cx + gateWidth / 2 - pillarWidth, cy - gateHeight + archHeight);
    ctx.lineTo(cx + gateWidth / 2 - pillarWidth, cy);
    ctx.closePath();
    ctx.fill();

    // Left pillar - front face
    ctx.fillStyle = colors.stone;
    ctx.beginPath();
    ctx.moveTo(cx - gateWidth / 2 + pillarWidth, cy);
    ctx.lineTo(cx - gateWidth / 2 + pillarWidth, cy - gateHeight);
    ctx.lineTo(cx - gateWidth / 2 + pillarWidth + 5 * s, cy - gateHeight - 3 * s);
    ctx.lineTo(cx - gateWidth / 2 + pillarWidth + 5 * s, cy - 5 * s);
    ctx.closePath();
    ctx.fill();

    // Right pillar - front face
    ctx.beginPath();
    ctx.moveTo(cx + gateWidth / 2 - pillarWidth, cy);
    ctx.lineTo(cx + gateWidth / 2 - pillarWidth, cy - gateHeight);
    ctx.lineTo(cx + gateWidth / 2 - pillarWidth - 5 * s, cy - gateHeight - 3 * s);
    ctx.lineTo(cx + gateWidth / 2 - pillarWidth - 5 * s, cy - 5 * s);
    ctx.closePath();
    ctx.fill();

    // Arch top
    ctx.fillStyle = colors.light;
    ctx.beginPath();
    ctx.moveTo(cx - gateWidth / 2 + pillarWidth, cy - gateHeight + archHeight);
    ctx.quadraticCurveTo(cx, cy - gateHeight - 5 * s, cx + gateWidth / 2 - pillarWidth, cy - gateHeight + archHeight);
    ctx.quadraticCurveTo(cx, cy - gateHeight - 12 * s, cx - gateWidth / 2 + pillarWidth, cy - gateHeight + archHeight);
    ctx.fill();

    // Pillar decorations (top caps)
    ctx.fillStyle = colors.light;
    // Left cap
    ctx.beginPath();
    ctx.moveTo(cx - gateWidth / 2, cy - gateHeight);
    ctx.lineTo(cx - gateWidth / 2 + pillarWidth / 2, cy - gateHeight - 8 * s);
    ctx.lineTo(cx - gateWidth / 2 + pillarWidth, cy - gateHeight);
    ctx.closePath();
    ctx.fill();
    // Right cap
    ctx.beginPath();
    ctx.moveTo(cx + gateWidth / 2, cy - gateHeight);
    ctx.lineTo(cx + gateWidth / 2 - pillarWidth / 2, cy - gateHeight - 8 * s);
    ctx.lineTo(cx + gateWidth / 2 - pillarWidth, cy - gateHeight);
    ctx.closePath();
    ctx.fill();

    // Gate doors (wooden, partially open)
    ctx.fillStyle = colors.wood;
    // Left door
    ctx.beginPath();
    ctx.moveTo(cx - gateWidth / 2 + pillarWidth + 2 * s, cy - 5 * s);
    ctx.lineTo(cx - gateWidth / 2 + pillarWidth + 2 * s, cy - gateHeight + archHeight + 10 * s);
    ctx.lineTo(cx - 8 * s, cy - gateHeight + archHeight);
    ctx.lineTo(cx - 8 * s, cy - 10 * s);
    ctx.closePath();
    ctx.fill();

    // Door details (planks)
    ctx.strokeStyle = '#2a1a10';
    ctx.lineWidth = 1 * s;
    ctx.beginPath();
    ctx.moveTo(cx - gateWidth / 2 + pillarWidth + 8 * s, cy - 10 * s);
    ctx.lineTo(cx - gateWidth / 2 + pillarWidth + 10 * s, cy - gateHeight + archHeight + 15 * s);
    ctx.stroke();

    // Metal bands
    ctx.strokeStyle = '#3a3a3a';
    ctx.lineWidth = 2 * s;
    const bandY1 = cy - 20 * s;
    const bandY2 = cy - gateHeight + archHeight + 25 * s;
    ctx.beginPath();
    ctx.moveTo(cx - gateWidth / 2 + pillarWidth + 2 * s, bandY1);
    ctx.lineTo(cx - 10 * s, bandY1 - 3 * s);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx - gateWidth / 2 + pillarWidth + 2 * s, bandY2);
    ctx.lineTo(cx - 12 * s, bandY2 - 2 * s);
    ctx.stroke();
  }
}

// Wall Gate - Duvar Kapısı (smaller doorway)
export class WallGate extends PropAsset {
  readonly id = 'wall_gate';
  readonly width = 64;
  readonly height = 70;
  readonly walkable = true;
  readonly collisionRadius = 0;
  readonly occludesPlayer = false;

  draw(ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number, variant?: number): void {
    const v = variant || 0;

    const colors = [
      { main: '#6b6b6b', dark: '#4a4a4a', light: '#8a8a8a' },
      { main: '#8b7355', dark: '#5a4a3a', light: '#a08570' },
    ][v % 2];

    const width = 50 * s;
    const height = 55 * s;
    const doorWidth = 24 * s;
    const doorHeight = 40 * s;

    // Shadow
    ctx.fillStyle = 'rgba(0,0,0,0.25)';
    ctx.beginPath();
    ctx.ellipse(cx, cy - 2 * s, 25 * s, 10 * s, 0, 0, Math.PI * 2);
    ctx.fill();

    // Wall section
    ctx.fillStyle = colors.dark;
    ctx.beginPath();
    ctx.moveTo(cx - width / 2, cy);
    ctx.lineTo(cx - width / 2, cy - height);
    ctx.lineTo(cx, cy - height - 8 * s);
    ctx.lineTo(cx, cy);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = colors.main;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx, cy - height - 8 * s);
    ctx.lineTo(cx + width / 2, cy - height);
    ctx.lineTo(cx + width / 2, cy);
    ctx.closePath();
    ctx.fill();

    // Door opening (dark)
    ctx.fillStyle = '#0a0805';
    ctx.fillRect(cx - doorWidth / 2, cy - doorHeight, doorWidth, doorHeight);

    // Door arch
    ctx.beginPath();
    ctx.arc(cx, cy - doorHeight, doorWidth / 2, Math.PI, 0);
    ctx.fill();

    // Top
    ctx.fillStyle = colors.light;
    ctx.beginPath();
    ctx.moveTo(cx - width / 2, cy - height);
    ctx.lineTo(cx, cy - height - 8 * s);
    ctx.lineTo(cx + width / 2, cy - height);
    ctx.lineTo(cx, cy - height + 4 * s);
    ctx.closePath();
    ctx.fill();
  }
}
